import React, { useEffect, useState, useCallback, useRef } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import {
  apiRequestBodyBuilder,
  getCurrencySymbol,
} from "../config/tableHelper";
import MetricsModal from "../Tools/ModifyChartColumns";

const API_URL = "https://api.confidanto.com/audience/chart";

// Restored classic variations of Dark Blue, Indigo, Cyan, and Teal
export const dynamicPalette = [
  "#1565C0", // Dark Blue (Blue 800)
  "#3F51B5", // Indigo 500
  "#00ACC1", // Cyan 600
  "#26A69A", // Teal 400
  "#2196F3", // Blue 500
  "#64B5F6", // Blue 300
  "#9FA8DA", // Indigo 200
  "#80DEEA", // Cyan 200
];

const METRIC_COLORS = {
  clicks: { border: "#1565C0" },
  cost: { border: "#00ACC1" },
  average_cpc: { border: "#3F51B5" },
  ctr: { border: "#26A69A" },
  conversions: { border: "#2196F3" },
  impressions: { border: "#64B5F6" },
};

export const ALL_METRICS = [
  { id: "1", title: "Clicks", key: "clicks", type: "number", category: "Performance" },
  { id: "2", title: "Impr.", key: "impressions", type: "number", category: "Performance" },
  { id: "3", title: "Cost", key: "cost", type: "currency", category: "Performance" },
  { id: "4", title: "Conversions", key: "conversions", type: "number", category: "Conversions" },
  { id: "5", title: "CTR", key: "ctr", type: "float", category: "Performance" },
  { id: "6", title: "Average CPC", key: "average_cpc", type: "currency", category: "Performance" },
  { id: "7", title: "Conv rate", key: "conversion_rate", type: "float", category: "Conversions" },
  { id: "8", title: "Cost/Conv", key: "cost_per_conversion", type: "currency", category: "Conversions" },
  { id: "9", title: "Revenue", key: "revenue", type: "currency", category: "Shopping / Revenue" },
  { id: "10", title: "ROAS", key: "roas", type: "float", category: "Shopping / Revenue" },
  { id: "11", title: "Interaction Rate", key: "interaction_rate", type: "float", category: "Performance" },
  { id: "12", title: "Interactions", key: "interactions", type: "number", category: "Performance" },
  { id: "90", title: "Avg. CPM", key: "average_cpm", type: "currency", category: "Performance", visible: false },
  { id: "91", title: "Avg. Cost", key: "average_cost", type: "currency", category: "Performance", visible: false },
  { id: "92", title: "Avg. CPV", key: "average_cpv", type: "currency", category: "Performance", visible: false },
];

const getMetricColor = (metricKey, index = 0) => {
  return (
    METRIC_COLORS[metricKey]?.border ||
    dynamicPalette[index % dynamicPalette.length]
  );
};

const formatDateRange = (startDateStr, endDateStr) => {
  if (!startDateStr || !endDateStr) return "";
  try {
    const s = new Date(startDateStr);
    const e = new Date(endDateStr);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {
      return `${startDateStr} – ${endDateStr}`;
    }
    const sMonth = s.toLocaleString("default", { month: "short" });
    const eMonth = e.toLocaleString("default", { month: "short" });
    const sDay = s.getDate();
    const eDay = e.getDate();
    const sYear = s.getFullYear();
    const eYear = e.getFullYear();

    if (sYear === eYear) {
      if (sMonth === eMonth) {
        return `${sMonth} ${sDay} – ${eDay}, ${sYear}`;
      }
      return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${sYear}`;
    }
    return `${sMonth} ${sDay}, ${sYear} – ${eMonth} ${eDay}, ${eYear}`;
  } catch {
    return `${startDateStr} – ${endDateStr}`;
  }
};

const AgeChart = ({
  metric: initialMetric = "cost",
  previousDates,
  updateTableMetric,
  arrayIndex,
  PageType,
}) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState(null);

  const [chartType, setChartType] = useState("pie");
  const [selectedMetrics, setSelectedMetrics] = useState([initialMetric]);

  const [isAdjustOpen, setIsAdjustOpen] = useState(false);
  const adjustMenuRef = useRef(null);
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);

  const [hiddenAges, setHiddenAges] = useState([]);
  const [hiddenMetrics, setHiddenMetrics] = useState([]);

  const selectionLimit = chartType === "pie" ? 1 : 3;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        adjustMenuRef.current &&
        !adjustMenuRef.current.contains(event.target)
      ) {
        setIsAdjustOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchChartData = useCallback(async () => {
    const startDate = previousDates?.curr_start_date;
    const endDate = previousDates?.curr_end_date;
    const prevStartDate = previousDates?.prev_start_date;
    const prevEndDate = previousDates?.prev_end_date;

    if (!startDate || !endDate) {
      setError("Invalid date range provided.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const metricsToFetch = chartType === "pie" ? [selectedMetrics[0]] : selectedMetrics;
      const fetchedDataMap = new Map();

      await Promise.all(
        metricsToFetch.map(async (m) => {
          const requestBody = apiRequestBodyBuilder(null, {
            metric: m,
            start_date: startDate,
            end_date: endDate,
            reporting_type: PageType,
          });

          if (prevStartDate && prevEndDate) {
            requestBody.previous_start_date = prevStartDate;
            requestBody.previous_end_date = prevEndDate;
          }

          const response = await fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            throw new Error(`API error: ${response.statusText}`);
          }

          const result = await response.json();
          const apiData = result.age_chart_data || result[0]?.age_chart_data || [];

          apiData.forEach((item) => {
            const ageLabel = String(item.age_range || "")
              .replace("AGE_RANGE_", "")
              .replace("_", "-")
              .replace("UP", "+");

            if (!fetchedDataMap.has(ageLabel)) {
              fetchedDataMap.set(ageLabel, {
                id: ageLabel, // Use ageLabel as a stable ID
                label: ageLabel,
                metrics: {},
              });
            }

            const dataObj = fetchedDataMap.get(ageLabel);
            const metricData = item[m];
            let currentVal = 0;
            let previousVal = null;

            if (metricData !== undefined && metricData !== null) {
              if (typeof metricData === "object" && "current" in metricData) {
                currentVal = metricData.current;
                if ("previous" in metricData) previousVal = metricData.previous;
              } else {
                currentVal = metricData;
              }
            }

            dataObj.metrics[m] = {
              current: Number(currentVal) || 0,
              previous: previousVal !== null ? Number(previousVal) || 0 : null,
            };
          });
        })
      );

      setChartData(Array.from(fetchedDataMap.values()));
    } catch (err) {
      setError("Failed to fetch chart data.");
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMetrics, chartType, previousDates, PageType]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const handleChartTypeChange = (e) => {
    const newType = e.target.value;
    setChartType(newType);
    if (newType === "pie" && selectedMetrics.length > 1) {
      setSelectedMetrics([selectedMetrics[0]]);
    }
  };

  const handleApplyMetrics = (newMetrics) => {
    const oldPrimary = selectedMetrics[0];
    setSelectedMetrics(newMetrics);
    setHiddenMetrics([]);
    setIsMetricsModalOpen(false);
    if (updateTableMetric) {
      updateTableMetric(arrayIndex, oldPrimary, newMetrics[0]);
    }
  };

  const formatValue = (value, metricKey) => {
    if (value === null || value === undefined) return "";
    const column = ALL_METRICS.find((item) => item.key === metricKey);
    const finalValue = Number(value).toLocaleString();
    if (!column) return finalValue;

    switch (column.type) {
      case "float":
        return `${finalValue}%`;
      case "currency":
        return `${getCurrencySymbol()}${finalValue}`;
      default:
        return finalValue;
    }
  };

  const formatYAxisValue = (value, metricKey) => {
    if (value === null || value === undefined) return "";
    const column = ALL_METRICS.find((item) => item.key === metricKey);
    const compactVal = Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);

    if (selectedMetrics.length > 1) {
      return compactVal;
    }

    switch (column?.type) {
      case "float":
        return `${compactVal}%`;
      case "currency":
        return `${getCurrencySymbol()}${compactVal}`;
      default:
        return compactVal;
    }
  };

  const renderChart = () => {
    if (chartData.length === 0) {
      return (
        <div className="text-center text-slate-400 py-12 text-sm font-medium">
          No data available.
        </div>
      );
    }

    if (chartType === "pie") {
      const metricKey = selectedMetrics[0];
      const metricInfo = ALL_METRICS.find((item) => item.key === metricKey);
      const metricTitle = metricInfo?.title || metricKey;

      const hasMeaningfulCurrent = chartData.some((d) => (d.metrics[metricKey]?.current || 0) > 0);
      const hasMeaningfulPrevious = chartData.some((d) => (d.metrics[metricKey]?.previous || 0) > 0);

      const chartSeriesData = chartData.map((d, index) => ({
        id: d.id,
        label: d.label,
        color: dynamicPalette[index % dynamicPalette.length],
        currentValue: d.metrics[metricKey]?.current || 0,
        previousValue: d.metrics[metricKey]?.previous || 0,
      }));

      const visibleData = chartSeriesData.filter((d) => !hiddenAges.includes(d.id));
      const hasPrevious = chartSeriesData.some((d) => d.previousValue !== 0);

      const totalCurrent = visibleData.reduce((sum, d) => sum + (d.currentValue || 0), 0);
      const totalPrevious = visibleData.reduce((sum, d) => sum + (d.previousValue || 0), 0);

      const currDateText = formatDateRange(previousDates?.curr_start_date, previousDates?.curr_end_date);
      const prevDateText = formatDateRange(previousDates?.prev_start_date, previousDates?.prev_end_date);

      const customLegend = (
        <div className="w-full max-h-[340px] overflow-y-auto">
          <div className="w-full border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[12px] font-semibold text-slate-700">
                  <th className="py-2.5 px-3">Age Group</th>
                  <th className="py-2.5 px-3 text-right">Current</th>
                  {hasPrevious && <th className="py-2.5 px-3 text-right">Previous</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-[13px]">
                {chartSeriesData.map((d) => {
                  const isHidden = hiddenAges.includes(d.id);
                  return (
                    <tr
                      key={d.id}
                      onClick={() =>
                        setHiddenAges((prev) =>
                          prev.includes(d.id) ? prev.filter((id) => id !== d.id) : [...prev, d.id]
                        )
                      }
                      className={`cursor-pointer transition-colors duration-150 ${
                        isHidden ? "opacity-40 bg-slate-50/50" : "hover:bg-slate-50/80"
                      }`}
                    >
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <div className="flex items-center gap-2.5">
                          <span
                            className={`w-2.5 h-2.5 rounded-full shrink-0 ${isHidden ? "opacity-40" : ""}`}
                            style={{ backgroundColor: d.color }}
                          />
                          <span
                            className={`font-semibold ${
                              isHidden ? "line-through text-slate-400" : "text-slate-800"
                            }`}
                          >
                            {d.label}
                          </span>
                        </div>
                      </td>
                      <td
                        className={`py-2.5 px-3 text-right font-semibold tabular-nums whitespace-nowrap ${
                          isHidden ? "text-slate-400" : "text-slate-900"
                        }`}
                      >
                        {formatValue(d.currentValue, metricKey)}
                      </td>
                      {hasPrevious && (
                        <td
                          className={`py-2.5 px-3 text-right font-semibold tabular-nums whitespace-nowrap ${
                            isHidden ? "text-slate-400" : "text-slate-600"
                          }`}
                        >
                          {formatValue(d.previousValue, metricKey)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );

      return (
        <div className="flex flex-col lg:flex-row w-full h-full items-center gap-8 py-2">
          {/* Charts Section */}
          <div className="flex-1 flex flex-col md:flex-row w-full gap-6 justify-center items-center">
            {/* Current Donut */}
            <div className="flex-1 flex flex-col items-center w-full">
              {hasPrevious && (
                <div className="flex flex-col items-center mb-2 text-center">
                  <div className="flex items-center gap-1 text-slate-800 font-bold text-sm">
                    <span>Current Period</span>
                    <svg
                      className="w-3.5 h-3.5 text-slate-400"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                  {currDateText && (
                    <span className="text-xs text-blue-600 font-medium mt-0.5">
                      {currDateText}
                    </span>
                  )}
                </div>
              )}

              {hasMeaningfulCurrent ? (
                <div className="relative flex items-center justify-center">
                  <PieChart
                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    series={[
                      {
                        data: visibleData.map((d) => ({
                          id: d.id,
                          value: d.currentValue,
                          label: d.label,
                          color: d.color,
                        })),
                        // Thick & chunky donut ring with small inner hole
                        innerRadius: 42,
                        outerRadius: 108,
                        paddingAngle: 0.5,
                        valueFormatter: (item) => formatValue(item.value, metricKey),
                        highlightScope: { faded: "global", highlight: "item" },
                        faded: { innerRadius: 36, additionalRadius: -8, color: "gray" },
                      },
                    ]}
                    slotProps={{ legend: { hidden: true } }}
                    height={240}
                    width={240}
                  />
                  {/* Total Value inside Donut Hole */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-base font-extrabold text-slate-900 tracking-tight leading-none">
                      {formatValue(totalCurrent, metricKey)}
                    </span>
                    <span className="text-[10px] font-semibold text-slate-500 mt-1 leading-none uppercase tracking-wide">
                      Total
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-400 h-[240px] flex items-center italic">
                  No data
                </div>
              )}
            </div>

            {/* Vertical Separator for Compare mode */}
            {hasPrevious && <div className="hidden md:block w-px h-56 bg-slate-200/80" />}

            {/* Previous Donut */}
            {hasPrevious && (
              <div className="flex-1 flex flex-col items-center w-full border-t md:border-t-0 border-slate-200 pt-4 md:pt-0">
                <div className="flex flex-col items-center mb-2 text-center">
                  <div className="flex items-center gap-1 text-slate-800 font-bold text-sm">
                    <span>Previous Period</span>
                    <svg
                      className="w-3.5 h-3.5 text-slate-400"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </div>
                  {prevDateText && (
                    <span className="text-xs text-blue-600 font-medium mt-0.5">
                      {prevDateText}
                    </span>
                  )}
                </div>

                {hasMeaningfulPrevious ? (
                  <div className="relative flex items-center justify-center">
                    <PieChart
                      margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                      series={[
                        {
                          data: visibleData.map((d) => ({
                            id: d.id,
                            value: d.previousValue,
                            label: d.label,
                            color: d.color,
                          })),
                          // Thick & chunky donut ring with small inner hole
                          innerRadius: 42,
                          outerRadius: 108,
                          paddingAngle: 0.5,
                          valueFormatter: (item) => formatValue(item.value, metricKey),
                          highlightScope: { faded: "global", highlight: "item" },
                          faded: { innerRadius: 36, additionalRadius: -8, color: "gray" },
                        },
                      ]}
                      slotProps={{ legend: { hidden: true } }}
                      height={240}
                      width={240}
                    />
                    {/* Total Value inside Donut Hole */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-base font-extrabold text-slate-900 tracking-tight leading-none">
                        {formatValue(totalPrevious, metricKey)}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-500 mt-1 leading-none uppercase tracking-wide">
                        Total
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 h-[240px] flex items-center italic">
                    No data
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Table Legend */}
          <div className="w-full lg:w-auto min-w-[280px] lg:min-w-[340px] max-w-full lg:max-w-[45%] border-t lg:border-t-0 lg:border-l border-slate-200 pt-4 lg:pt-0 lg:pl-6">
            {customLegend}
          </div>
        </div>
      );
    }

    const seriesData = [];
    selectedMetrics.forEach((m, index) => {
      if (hiddenMetrics.includes(m)) return;

      const metricInfo = ALL_METRICS.find((item) => item.key === m);
      const title = metricInfo?.title || m;
      const hasPrevious = chartData.some(
        (d) => d.metrics[m]?.previous !== null && d.metrics[m]?.previous !== undefined
      );

      const baseColor = getMetricColor(m, index);
      const isCost = m === "cost";

      seriesData.push({
        id: m,
        data: chartData.map((d) => d.metrics[m]?.current || 0),
        label: hasPrevious ? `${title} (Current)` : title,
        valueFormatter: (v) => formatValue(v, m),
        color: baseColor,
        yAxisId: isCost && selectedMetrics.length > 1 ? "rightAxis" : "leftAxis",
        showMark: chartType === "line",
        ...(chartType === "line" && { area: true, curve: "catmullRom" }),
      });

      if (hasPrevious) {
        seriesData.push({
          id: `${m}_prev`,
          data: chartData.map((d) => d.metrics[m]?.previous || 0),
          label: `${title} (Previous)`,
          valueFormatter: (v) => formatValue(v, m),
          color: `${baseColor}80`,
          yAxisId: isCost && selectedMetrics.length > 1 ? "rightAxis" : "leftAxis",
          showMark: chartType === "line",
          ...(chartType === "line" && { area: false, curve: "catmullRom" }),
        });
      }
    });

    const xAxisConfig = [
      {
        scaleType: chartType === "bar" ? "band" : "point",
        data: chartData.map((item) => item.label),
        tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
      },
    ];

    const hasCost = selectedMetrics.includes("cost") && selectedMetrics.length > 1;

    const yAxisConfig = [
      {
        id: "leftAxis",
        position: "left",
        valueFormatter: (v) =>
          formatYAxisValue(v, selectedMetrics.find((x) => x !== "cost") || selectedMetrics[0]),
        tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
      },
    ];

    if (hasCost) {
      yAxisConfig.push({
        id: "rightAxis",
        position: "right",
        valueFormatter: (v) => formatYAxisValue(v, "cost"),
        tickLabelStyle: { fill: "#94A3B8", fontSize: 11 },
      });
    }

    const ChartComponent = chartType === "line" ? LineChart : BarChart;

    const gradientDefs = selectedMetrics.map((m) => {
      const colorInfo = METRIC_COLORS[m] || { border: getMetricColor(m) };
      return (
        <linearGradient id={`area-gradient-${m}`} key={m} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colorInfo.border} stopOpacity="0.35" />
          <stop offset="100%" stopColor={colorInfo.border} stopOpacity="0.0" />
        </linearGradient>
      );
    });

    const sxRules = {
      ...selectedMetrics.reduce((acc, m, idx) => {
        const color = getMetricColor(m, idx);
        acc[`& .MuiAreaElement-series-${m}`] = {
          fill: `url(#area-gradient-${m}) !important`,
        };
        acc[`& .MuiMarkElement-series-${m}`] = {
          fill: `${color} !important`,
          stroke: "#ffffff !important",
          strokeWidth: 2,
          r: 4,
        };
        return acc;
      }, {}),
      "& .MuiChartsGrid-line": {
        strokeDasharray: "4 4",
        stroke: "#F1F5F9",
      },
    };

    return (
      <div className="flex flex-col w-full h-[360px] justify-center relative">
        <ChartComponent
          xAxis={xAxisConfig}
          yAxis={yAxisConfig}
          series={seriesData}
          height={350}
          margin={{ left: 50, right: hasCost ? 50 : 20, top: 20, bottom: 40 }}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={sxRules}
        >
          {chartType === "line" && <defs>{gradientDefs}</defs>}
        </ChartComponent>
      </div>
    );
  };

  return (
    <div className="w-full bg-white border border-slate-100 rounded-2xl shadow-sm p-6 flex flex-col gap-4 transition-all">
      {/* Header Row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-base font-semibold text-slate-800">
          Age Demographics Overview
        </h2>

        {/* Right Controls: Chart Type Selector + Adjust Menu */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={chartType}
              onChange={handleChartTypeChange}
              className="appearance-none bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 pl-3 pr-8 py-2 hover:bg-slate-50 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="line">Line Chart</option>
              <option value="bar">Bar Chart</option>
              <option value="pie">Pie Chart</option>
            </select>
            <svg
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          <div className="relative" ref={adjustMenuRef}>
            <button
              onClick={() => setIsAdjustOpen(!isAdjustOpen)}
              className="p-2 border border-slate-200 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all focus:outline-none"
              aria-label="Modify Metrics Settings"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>

            {isAdjustOpen && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-4 origin-top-right">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 pb-2 border-b border-slate-100">
                  Metrics Settings
                </h3>
                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Select Metrics ({selectedMetrics.length}/{selectionLimit})
                  </label>
                  <button
                    onClick={() => {
                      setIsMetricsModalOpen(true);
                      setIsAdjustOpen(false);
                    }}
                    className="w-full text-left bg-white border border-slate-200 text-xs font-medium text-slate-700 rounded-lg hover:bg-slate-50 p-2.5 transition-all flex justify-between items-center"
                  >
                    <span className="truncate mr-2 block">
                      {selectedMetrics
                        .map((m) => ALL_METRICS.find((x) => x.key === m)?.title)
                        .join(", ") || "Select metrics..."}
                    </span>
                    <svg
                      className="text-slate-400 shrink-0"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Left Metric Legend Placement for Line & Bar */}
      {chartType !== "pie" && (
        <div className="flex flex-wrap items-center gap-6 mt-1">
          {selectedMetrics.map((m, index) => {
            const metricInfo = ALL_METRICS.find((item) => item.key === m);
            const title = metricInfo?.title || m;
            const isHidden = hiddenMetrics.includes(m);
            const color = getMetricColor(m, index);

            return (
              <div
                key={m}
                onClick={() =>
                  setHiddenMetrics((prev) =>
                    prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
                  )
                }
                className="flex items-center gap-2 cursor-pointer group"
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full inline-block transition-all duration-200 ${
                    isHidden ? "opacity-30 scale-75" : "shadow-sm"
                  }`}
                  style={{ backgroundColor: color }}
                />
                <span
                  className={`text-xs font-medium transition-all duration-200 ${
                    isHidden
                      ? "line-through text-slate-400 font-normal"
                      : "text-slate-600 group-hover:text-slate-900"
                  }`}
                >
                  {title}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Chart Canvas Container */}
      <div className="w-full min-h-[350px] relative">
        {loading ? (
          <div className="flex items-center justify-center min-h-[350px] text-slate-400 text-xs font-medium animate-pulse">
            Loading performance data...
          </div>
        ) : error ? (
          <div className="flex items-center justify-center min-h-[350px] text-red-500 text-xs font-medium">
            {error}
          </div>
        ) : (
          renderChart()
        )}
      </div>

      <MetricsModal
        isOpen={isMetricsModalOpen}
        onClose={() => setIsMetricsModalOpen(false)}
        allMetrics={ALL_METRICS}
        initialSelectedMetrics={selectedMetrics}
        onApply={handleApplyMetrics}
        selectionLimit={selectionLimit}
      />
    </div>
  );
};

export default AgeChart;